import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Box,
  Typography,
  Chip,
  Divider,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  FilterState,
  FilterGroup,
  FilterCondition,
  EntityType,
  FieldDefinition,
  FilterOperator,
  getFieldsForEntity,
  OPERATOR_LABELS,
} from "./FilterBuilder.types";
import {
  createEmptyFilterState,
  createEmptyCondition,
  createEmptyGroup,
  generateId,
  convertFilterToMongoDB,
  validateCondition,
  countActiveFilters,
} from "./FilterBuilder.utils";
import { saveFilter, loadFilter } from "./FilterBuilder.storage";

interface FilterBuilderProps {
  entityType: EntityType;
  open: boolean;
  onClose: () => void;
  onApply: (filter: string | null) => void;
  pageId?: string;
}

const FilterBuilder: React.FC<FilterBuilderProps> = ({
  entityType,
  open,
  onClose,
  onApply,
  pageId,
}) => {
  const fields = getFieldsForEntity(entityType);
  const [filterState, setFilterState] = useState<FilterState>(createEmptyFilterState());
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      // Load saved filter or create empty one
      const saved = loadFilter(entityType, pageId);
      if (saved) {
        setFilterState(saved);
      } else {
        setFilterState(createEmptyFilterState());
      }
      setErrors({});
    }
  }, [open, entityType, pageId]);

  const updateCondition = useCallback(
    (groupId: string, conditionId: string, updates: Partial<FilterCondition>) => {
      setFilterState((prev) => {
        const updateGroup = (group: FilterGroup): FilterGroup => {
          if (group.id === groupId) {
            return {
              ...group,
              conditions: group.conditions.map((c) =>
                c.id === conditionId ? { ...c, ...updates } : c
              ),
            };
          }
          return {
            ...group,
            groups: group.groups.map(updateGroup),
          };
        };
        return {
          rootGroup: updateGroup(prev.rootGroup),
        };
      });
    },
    []
  );

  const addCondition = useCallback((groupId: string) => {
    setFilterState((prev) => {
      const updateGroup = (group: FilterGroup): FilterGroup => {
        if (group.id === groupId) {
          return {
            ...group,
            conditions: [...group.conditions, createEmptyCondition()],
          };
        }
        return {
          ...group,
          groups: group.groups.map(updateGroup),
        };
      };
      return {
        rootGroup: updateGroup(prev.rootGroup),
      };
    });
  }, []);

  const removeCondition = useCallback((groupId: string, conditionId: string) => {
    setFilterState((prev) => {
      const updateGroup = (group: FilterGroup): FilterGroup => {
        if (group.id === groupId) {
          return {
            ...group,
            conditions: group.conditions.filter((c) => c.id !== conditionId),
          };
        }
        return {
          ...group,
          groups: group.groups.map(updateGroup),
        };
      };
      return {
        rootGroup: updateGroup(prev.rootGroup),
      };
    });
  }, []);

  const addGroup = useCallback((groupId: string) => {
    setFilterState((prev) => {
      const updateGroup = (group: FilterGroup): FilterGroup => {
        if (group.id === groupId) {
          return {
            ...group,
            groups: [...group.groups, createEmptyGroup()],
          };
        }
        return {
          ...group,
          groups: group.groups.map(updateGroup),
        };
      };
      return {
        rootGroup: updateGroup(prev.rootGroup),
      };
    });
  }, []);

  const removeGroup = useCallback((parentGroupId: string, groupId: string) => {
    setFilterState((prev) => {
      const updateGroup = (group: FilterGroup): FilterGroup => {
        if (group.id === parentGroupId) {
          return {
            ...group,
            groups: group.groups.filter((g) => g.id !== groupId),
          };
        }
        return {
          ...group,
          groups: group.groups.map(updateGroup),
        };
      };
      return {
        rootGroup: updateGroup(prev.rootGroup),
      };
    });
  }, []);

  const setGroupLogic = useCallback((groupId: string, logic: "AND" | "OR") => {
    setFilterState((prev) => {
      const updateGroup = (group: FilterGroup): FilterGroup => {
        if (group.id === groupId) {
          return {
            ...group,
            logic,
          };
        }
        return {
          ...group,
          groups: group.groups.map(updateGroup),
        };
      };
      return {
        rootGroup: updateGroup(prev.rootGroup),
      };
    });
  }, []);

  const getFieldDefinition = (fieldKey: string): FieldDefinition | undefined => {
    return fields.find((f) => f.key === fieldKey);
  };

  const handleApply = () => {
    // Validate all conditions
    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    const validateGroup = (group: FilterGroup, path: string = "") => {
      group.conditions.forEach((condition) => {
        if (condition.field) {
          const validation = validateCondition(condition, fields);
          if (!validation.valid) {
            newErrors[condition.id] = validation.error || "Invalid condition";
            hasErrors = true;
          }
        }
      });
      group.groups.forEach((g, index) => {
        validateGroup(g, `${path}.groups[${index}]`);
      });
    };

    validateGroup(filterState.rootGroup);

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    const mongoQuery = convertFilterToMongoDB(filterState, entityType);
    saveFilter(entityType, filterState, pageId);
    onApply(mongoQuery);
    onClose();
  };

  const handleClear = () => {
    setFilterState(createEmptyFilterState());
    setErrors({});
  };

  const handleCancel = () => {
    // Restore saved filter
    const saved = loadFilter(entityType, pageId);
    if (saved) {
      setFilterState(saved);
    } else {
      setFilterState(createEmptyFilterState());
    }
    setErrors({});
    onClose();
  };

  const renderCondition = (
    condition: FilterCondition,
    groupId: string,
    depth: number = 0
  ) => {
    const fieldDef = condition.field ? getFieldDefinition(condition.field) : undefined;
    const availableOperators = fieldDef?.operators || [];
    const error = errors[condition.id];

    const handleValueChange = (newValue: any) => {
      updateCondition(groupId, condition.id, { value: newValue });
    };

    const handleListValueChange = (values: string[]) => {
      updateCondition(groupId, condition.id, { value: values });
    };

    return (
      <Box
        key={condition.id}
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-start",
          mb: 2,
          pl: depth * 4,
          borderLeft: depth > 0 ? "2px solid #e0e0e0" : "none",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Field</InputLabel>
          <Select
            value={condition.field || ""}
            label="Field"
            onChange={(e) => {
              const field = e.target.value;
              const def = getFieldDefinition(field);
              updateCondition(groupId, condition.id, {
                field,
                operator: def?.operators[0] || "equals",
                value: def?.type === "boolean" ? true : "",
              });
            }}
          >
            {fields.map((field) => (
              <MenuItem key={field.key} value={field.key}>
                {field.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Operator</InputLabel>
          <Select
            value={condition.operator}
            label="Operator"
            onChange={(e) => {
              updateCondition(groupId, condition.id, {
                operator: e.target.value as FilterOperator,
                value: condition.value,
              });
            }}
            disabled={!condition.field}
          >
            {availableOperators.map((op) => (
              <MenuItem key={op} value={op}>
                {OPERATOR_LABELS[op]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {condition.operator !== "isNull" && condition.operator !== "isNotNull" && (
          <>
            {condition.operator === "inList" || condition.operator === "notInList" ? (
              <Autocomplete
                multiple
                freeSolo
                size="small"
                options={[]}
                value={Array.isArray(condition.value) ? condition.value : []}
                onChange={(_, newValue) => handleListValueChange(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Values (comma-separated)"
                    placeholder="Type and press Enter"
                    error={!!error}
                    helperText={error}
                  />
                )}
                sx={{ minWidth: 300, flex: 1 }}
              />
            ) : fieldDef?.type === "boolean" ? (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Value</InputLabel>
                <Select
                  value={condition.value === true || condition.value === "true" ? "true" : "false"}
                  label="Value"
                  onChange={(e) => handleValueChange(e.target.value === "true")}
                >
                  <MenuItem value="true">True</MenuItem>
                  <MenuItem value="false">False</MenuItem>
                </Select>
              </FormControl>
            ) : fieldDef?.type === "date" ? (
              <TextField
                size="small"
                type="date"
                label="Value"
                value={condition.value || ""}
                onChange={(e) => handleValueChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!!error}
                helperText={error}
                sx={{ minWidth: 200 }}
              />
            ) : fieldDef?.type === "number" ? (
              <TextField
                size="small"
                type="number"
                label="Value"
                value={condition.value || ""}
                onChange={(e) => handleValueChange(Number(e.target.value))}
                error={!!error}
                helperText={error}
                sx={{ minWidth: 200 }}
              />
            ) : fieldDef?.type === "enum" ? (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Value</InputLabel>
                <Select
                  value={condition.value || ""}
                  label="Value"
                  onChange={(e) => handleValueChange(e.target.value)}
                  error={!!error}
                >
                  {fieldDef.enumValues?.map((val) => (
                    <MenuItem key={val} value={val}>
                      {val}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                size="small"
                label="Value"
                value={condition.value || ""}
                onChange={(e) => handleValueChange(e.target.value)}
                error={!!error}
                helperText={error}
                sx={{ minWidth: 300, flex: 1 }}
              />
            )}
          </>
        )}

        <IconButton
          size="small"
          onClick={() => removeCondition(groupId, condition.id)}
          color="error"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  const renderGroup = (group: FilterGroup, parentId: string | null, depth: number = 0) => {
    return (
      <Box
        key={group.id}
        sx={{
          mb: 2,
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          bgcolor: depth % 2 === 0 ? "#fafafa" : "#ffffff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
          <Button
            variant={group.logic === "AND" ? "contained" : "outlined"}
            size="small"
            onClick={() => setGroupLogic(group.id, "AND")}
            sx={{ minWidth: 80 }}
          >
            AND
          </Button>
          <Button
            variant={group.logic === "OR" ? "contained" : "outlined"}
            size="small"
            onClick={() => setGroupLogic(group.id, "OR")}
            sx={{ minWidth: 80 }}
          >
            OR
          </Button>
          <Typography variant="body2" color="text.secondary">
            Group
          </Typography>
          {parentId && (
            <IconButton
              size="small"
              onClick={() => removeGroup(parentId, group.id)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {group.conditions.map((condition) => renderCondition(condition, group.id, depth))}

        {group.groups.map((g) => renderGroup(g, group.id, depth + 1))}

        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => addCondition(group.id)}
            variant="outlined"
          >
            Add Condition
          </Button>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => addGroup(group.id)}
            variant="outlined"
          >
            Add Group
          </Button>
        </Box>
      </Box>
    );
  };

  const activeFilterCount = countActiveFilters(filterState);

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth>
      <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FilterListIcon />
            <Typography variant="h6">Advanced Filter</Typography>
            {activeFilterCount > 0 && (
              <Chip label={`${activeFilterCount} active`} size="small" color="primary" />
            )}
          </Box>
          <IconButton onClick={handleCancel}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {renderGroup(filterState.rootGroup, null, 0)}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClear} color="error" variant="outlined">
          Clear All
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleCancel} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleApply} variant="contained" color="primary">
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterBuilder;






